import React from 'react';
import { Icon } from '../../design-system/icons/Icon';
import { viewToggleMessages } from './tape-messages';
import type { IntlShape } from 'react-intl';

export type SrpViewMode = 'list' | 'map';

export interface ViewToggleProps {
  viewMode: SrpViewMode;
  onSelectList: () => void;
  onSelectMap: () => void;
  formatMessage: IntlShape['formatMessage'];
  className?: string;
}

/**
 * Small, labelled List | Map segmented control for the SRP filter tape / index strip (TEC navbar
 * redesign Phase 2 item 2.4): 24px tall, each 18px segment carrying both its glyph and its word.
 * Deliberately not a floating pill and not in row one: both were tried in the design mock and
 * rejected; it lives at the strip's right end (replacing the freshness stamp there) or beside
 * "Clear all" on the tape. This is a different (smaller, labelled) control from
 * components/map/MapViewModeToggle.tsx's icon-only segmented control used in SearchSidebar's
 * classic sub-row; that one is untouched (still used flag-off) and unrelated to this new one.
 */
export const ViewToggle: React.FC<ViewToggleProps> = ({ viewMode, onSelectList, onSelectMap, formatMessage, className = '' }) => {
  const activeClass = 'bg-jc-elevated text-jc-ink';
  const inactiveClass = 'text-jc-muted hover:text-jc-ink-secondary';
  return (
    <span
      role="group"
      aria-label={formatMessage({ id: 'search.view.ariaLabel', defaultMessage: 'View', description: 'Accessibility label for the list/map view segmented control' })}
      className={`inline-flex items-center h-6 p-0.5 rounded-[7px] border border-jc-border bg-jc-surface flex-shrink-0 ${className}`}
    >
      <button
        type="button"
        onClick={onSelectList}
        aria-pressed={viewMode === 'list'}
        className={`inline-flex items-center gap-1 h-[18px] px-[7px] rounded-[5px] text-[11.5px] font-semibold whitespace-nowrap transition-colors cursor-pointer ${
          viewMode === 'list' ? activeClass : inactiveClass
        }`}
      >
        <Icon name="bars4" useMaskForColor className={`w-3 h-3 ${viewMode === 'list' ? 'text-jc-accent' : ''}`} />
        {formatMessage(viewToggleMessages.list)}
      </button>
      <button
        type="button"
        onClick={onSelectMap}
        aria-pressed={viewMode === 'map'}
        className={`inline-flex items-center gap-1 h-[18px] px-[7px] rounded-[5px] text-[11.5px] font-semibold whitespace-nowrap transition-colors cursor-pointer ${
          viewMode === 'map' ? activeClass : inactiveClass
        }`}
      >
        <Icon name="mapPin" useMaskForColor className={`w-3 h-3 ${viewMode === 'map' ? 'text-jc-accent' : ''}`} />
        {formatMessage(viewToggleMessages.map)}
      </button>
    </span>
  );
};
